import React, { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * "The portal has been updated — refresh".
 *
 * A single-page app keeps running the bundle it was loaded with. Someone who
 * left a tab open on Monday is still working in Monday's portal on Wednesday:
 * a fixed bug is still broken for them, a new field simply isn't there, and
 * nothing on screen says so. They only find out by being confused.
 *
 * The build's own asset-manifest is the signal. Its hashed filename changes
 * exactly when a new frontend is deployed and at no other time — no version
 * endpoint to keep in sync, nothing to remember to bump. We compare it against
 * the bundle this tab is actually running, so a tab that is two deploys behind
 * is caught on the first check rather than having to wait for the next deploy.
 *
 * Deliberately quiet: a slow timer, a check when the tab comes back into focus
 * (the moment someone returns to a tab that sat idle for hours), and a card
 * that waits in the corner. It never reloads on its own — a reload throws away
 * whatever is half-typed on the page, so that stays the user's call.
 */

const CHECK_EVERY_MS = 3 * 60 * 1000;   // a deploy is worth knowing about, not urgent
const SNOOZE_MS = 30 * 60 * 1000;       // dismissed → stay out of the way

/** The bundle this tab is running, from the script tag that loaded it. */
const runningBundle = () => {
  const el = document.querySelector('script[src*="/static/js/main."]');
  if (!el) return null;  // dev server, or a build we don't recognise → stay silent
  try {
    return new URL(el.getAttribute("src"), window.location.origin).pathname;
  } catch (_) {
    return null;
  }
};

/** The bundle the server is handing out right now. */
const deployedBundle = async () => {
  const res = await fetch(`/asset-manifest.json?t=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) return null;
  const manifest = await res.json();
  const main = manifest && manifest.files && manifest.files["main.js"];
  if (typeof main !== "string") return null;
  return main.replace(/^https?:\/\/[^/]+/, "");
};

const COPY = {
  en: {
    title: "Portal updated",
    body: "You're on an older version of this page.",
    action: "Refresh",
    dismiss: "Dismiss",
  },
  uk: {
    title: "Портал оновлено",
    body: "У вас відкрита стара версія сторінки.",
    action: "Оновити",
    dismiss: "Закрити",
  },
};

const AppUpdateNotice = () => {
  const { user } = useAuth();
  const [stale, setStale] = useState(false);
  const mine = useRef(runningBundle());
  const quietUntil = useRef(0);

  useEffect(() => {
    if (!mine.current) return undefined;

    let stopped = false;
    const check = async () => {
      if (stopped || document.hidden || Date.now() < quietUntil.current) return;
      try {
        const live = await deployedBundle();
        if (!stopped && live && live !== mine.current) setStale(true);
      } catch (_) {
        // offline, or nginx restarting mid-deploy — the next check will tell us
      }
    };

    check();
    const timer = setInterval(check, CHECK_EVERY_MS);
    const onBack = () => { if (!document.hidden) check(); };
    document.addEventListener("visibilitychange", onBack);
    window.addEventListener("focus", onBack);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onBack);
      window.removeEventListener("focus", onBack);
    };
  }, []);

  const dismiss = useCallback(() => {
    quietUntil.current = Date.now() + SNOOZE_MS;
    setStale(false);
  }, []);

  if (!user || !stale) return null;

  // The client cabinet speaks Ukrainian; the rest of the portal is in English.
  const onClientSurface = window.location.pathname.startsWith("/client");
  const t = COPY[onClientSurface ? "uk" : "en"];

  return (
    <div
      // Above the support-chat bubble rather than bottom-left: on the left it
      // sat on top of the sidebar's Notifications and Logout, and a notice that
      // covers navigation is no longer unobtrusive. Measured — 8px clear of the
      // bubble.
      className="fixed bottom-36 right-6 z-50 w-[320px] max-w-[calc(100vw-3rem)] animate-in fade-in slide-in-from-bottom-2 duration-300"
      role="status"
      data-testid="app-update-notice"
    >
      <div className="flex items-start gap-3 rounded-xl border border-zinc-800 bg-zinc-900/95 px-4 py-3 shadow-lg backdrop-blur">
        <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-zinc-100">{t.title}</p>
          <p className="mt-0.5 text-xs text-zinc-400">{t.body}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-2 rounded-lg bg-orange-500/15 px-2.5 py-1 text-xs font-medium text-orange-400 transition-colors hover:bg-orange-500/25"
            data-testid="app-update-reload"
          >
            {t.action}
          </button>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t.dismiss}
          title={t.dismiss}
          className="-mr-1 -mt-1 shrink-0 rounded-md p-1 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-300"
          data-testid="app-update-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

export default AppUpdateNotice;
